// PreRegFormSetup.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormLayout from "../layouts/FormLayout";
import TopNavTabs from "../components/TopNavTabs";
import { isZh } from "../utils/language";
import { apiFetch, fetchLunarForIntDate } from "../utils/wkCommon";
import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";
import GearIcon from "../components/_icons";
import AgendaAttributeConfigModal from "../components/AgendaAttributeConfigModal";
import AgendaCrossNavMenu from "../components/AgendaCrossNavMenu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PreRegFormSetup() {
  const { id } = useParams();
  const documentId = id;

  const [agenda, setAgenda] = useState(null);
  const [lunarInfo, setLunarInfo] = useState(null);

  const [activeTab, setActiveTab] = useState("general");

  const [formCode, setFormCode] = useState("");
  const [titleZh, setTitleZh] = useState("");
  const [titleId, setTitleId] = useState("");
  const [descZh, setDescZh] = useState("");
  const [descId, setDescId] = useState("");
  const [instructionZh, setInstructionZh] = useState("");
  const [instructionId, setInstructionId] = useState("");

  const [allowRemarkOnInternal, setAllowRemarkOnInternal] = useState(false);
  const [allowRemarkOnExternal, setAllowRemarkOnExternal] = useState(false);
  
  const [showAttrModal, setShowAttrModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const [fields, setFields] = useState([]);

  /* ================= LOAD ================= */
  useEffect(() => {
    if (!documentId) return;

    async function load() {
      const resp = await apiFetch(
        `/api/agendas/${documentId}?populate%5B0%5D=agenda_pre_reg_form`
      );

      const data = resp?.data || resp;
      if (!data) return;

      setAgenda(data);

      if (data.agendaDate) {
        fetchLunarForIntDate(data.agendaDate)
          .then(setLunarInfo)
          .catch(() => setLunarInfo(null));
      }

      const form = data.agenda_pre_reg_form;

      /* =========================================
        JIKA FORM SUDAH ADA → LOAD DARI FORM
      ========================================= */
      if (form) {
        setFormCode(form.preRegFormCode || "");

        const cfg = form.preRegCustomFields || {};

        setTitleZh(cfg.titleZh || "");
        setTitleId(cfg.titleId || "");
        setDescZh(cfg.descZh || "");
        setDescId(cfg.descId || "");
        setInstructionZh(cfg.instructionZh || "");
        setInstructionId(cfg.instructionId || "");
        setAllowRemarkOnInternal(!!cfg.allowRemarkOnInternal);
        setAllowRemarkOnExternal(!!cfg.allowRemarkOnExternal);
        setFields(cfg.fields || []);

        return;
      }

      /* =========================================
        DEFAULT DARI AGENDA
      ========================================= */

      setTitleId(data.subject || "");
      setTitleZh(data.subject_zh || "");

      // optional default lain kalau mau
      setDescZh(data.description_zh || "");
      setDescId(data.description || "");

      setInstructionZh("");
      setInstructionId("");

      setAllowRemarkOnInternal(false);
      setAllowRemarkOnExternal(false);

      setFields([]);
    }

    load();
  }, [documentId]);

  /* ================= UTIL ================= */
  function getMMDD() {
    if (!agenda?.agendaDate) return "";
    const d = new Date(agenda.agendaDate);
    return `${String(d.getMonth() + 1).padStart(2, "0")}${String(
      d.getDate()
    ).padStart(2, "0")}`;
  }

  const cleanedCode =
    formCode.includes("_") ? formCode.split("_")[1] : formCode;

  const fullFormCode =
    agenda?.agendaDate && cleanedCode
      ? `${getMMDD()}_${cleanedCode}`
      : "";

  const previewUrl =
    agenda?.agendaDate
      ? `/f?${getMMDD()}_${cleanedCode || "{formCode}"}`
      : "";

  const entryLink =
    fullFormCode
      ? `${window.location.origin}/f?${fullFormCode}`
      : "";
            
  function getScopeText() {
    if (!agenda) return "";

    if (!agenda.allowUmatExternal && !agenda.allowPublic)
      return isZh() ? "僅限內部" : "Hanya Umat Internal";

    if (agenda.allowUmatExternal && !agenda.allowPublic)
      return isZh() ? "內部 + 外部" : "Internal (Li En) + Umat Fotang Lain";

    if (agenda.allowPublic)
      return isZh() ? "內部 + 公開" : "Internal + Publik/Umum";

    return "";
  }

  /* ================= DUPLICATE VALIDATION ================= */
  async function validateDuplicate(fullCode) {
    if (!fullCode) return false;

    const url =
      `/api/agendas?filters%5BpreRegFormCode%5D%5B$eq%5D=` +
      encodeURIComponent(fullCode);

    const resp = await apiFetch(url);
    const list = resp?.data || [];

    if (!list.length) return false;

    const duplicate = list.find(
      (item) => item.documentId !== documentId
    );

    return !!duplicate;
  }

  /* ================= FIELD MGMT ================= */
  function addField() {
    setFields([
      ...fields,
      {
        id: `fld_${Date.now()}`,

        /* BASIC */
        type: "text",

        labelZh: "",
        labelId: "",

        descriptionZh: "",
        descriptionId: "",

        /* SCOPE */
        scope: "all", // peserta | petugas | all

        /* VALIDATION */
        required: false,

        /* TYPE CONFIG */
        allowDecimal: false,

        /* OPTIONS */
        options: ["Option 1"],
        allowOther: false,
      },
    ]);
  }

  function updateField(i, key, val) {
    const copy = [...fields];
    copy[i][key] = val;
    setFields(copy);
  }

  function moveField(i, dir) {
    const copy = [...fields];
    const t = i + dir;
    if (t < 0 || t >= copy.length) return;
    [copy[i], copy[t]] = [copy[t], copy[i]];
    setFields(copy);
  }

  function removeField(i) {
    const copy = [...fields];
    copy.splice(i, 1);
    setFields(copy);
  }

  /* ================= SAVE ================= */
  const [saving, setSaving] = useState(false);
  async function handleSubmit() {
    if (saving) return;

    try {
      setSaving(true);

      if (!cleanedCode) {
        alert(isZh() ? "請輸入表單代碼" : "Kode Form wajib diisi");
        return;
      }

      const fullCode = `${getMMDD()}_${cleanedCode}`;
      const isDuplicate = await validateDuplicate(fullCode);

      if (isDuplicate) {
        alert(
          isZh()
            ? "此表單代碼已被使用"
            : "Duplikasi kode form, silakan gunakan kode lain."
        );
        return;
      }

      const existingResp = await apiFetch(
        `/api/agenda-pre-reg-forms?filters%5Bagenda%5D%5BdocumentId%5D%5B$eq%5D=${encodeURIComponent(documentId)}`
      );

      const existing =
        Array.isArray(existingResp?.data) &&
        existingResp.data.length
          ? existingResp.data[0]
          : null;

      const payload = {
        data: {
          agenda: documentId,
          preRegFormCode: fullCode,
          preRegCustomFields: {
            titleZh,
            titleId,
            descZh,
            descId,
            instructionZh,
            instructionId,
            allowRemarkOnInternal,
            allowRemarkOnExternal,
            formScope: agenda.allowPublic
              ? "internal_public"
              : agenda.allowUmatExternal
                ? "internal_external"
                : "internal",
            agendaDate: agenda.agendaDate,
            fields,
          },
        },
      };

      if (existing?.documentId) {
        await apiFetch(
          `/api/agenda-pre-reg-forms/${existing.documentId}`,
          {
            method: "PUT",
            body: payload,
          }
        );
      } else {
        await apiFetch(`/api/agenda-pre-reg-forms`, {
          method: "POST",
          body: payload,
        });
      }

      alert(isZh() ? "已儲存" : "Berhasil disimpan");
    } catch (err) {
      console.error(err);

      alert(
        isZh()
          ? "儲存失敗"
          : "Gagal menyimpan data"
      );
    } finally {
      setSaving(false);
    }
  }

  if (!agenda) return null;

  return (
    <>
      <FormLayout
        title={isZh() ? "預先報名表設定" : "Setup Form Registrasi"}
        submitLabel={
          saving
            ? (isZh() ? "儲存中..." : "Menyimpan...")
            : (isZh() ? "儲存" : "SIMPAN")
        }
        onSubmit={handleSubmit}
        headerActions={
          <>
            <button className="btn-icon" onClick={()=>setShowAttrModal(true)} title="議程設定 Konfigurasi Agenda">
              <GearIcon />
            </button>

            <div style={{ position: "relative" }}>
              <button className="btn-icon" onClick={()=>setShowMenu(s=>!s)}>⋯</button>

              {showMenu && (
                <div className="action-menu">
                  <AgendaCrossNavMenu
                    agendaId={id}
                    hasPreRegForm={!!agenda?.agenda_pre_reg_form}
                    onClose={()=>setShowMenu(false)}
                  />
                </div>
              )}
            </div>
          </>
        }
        rightPanelNode={
          <div className="prereg-right">
            <h4>{isZh() ? "報名連結" : "Entry Form Link"}</h4>

            <div className="share-box">
              {entryLink || "-"}
            </div>

            {fullFormCode && (
              <>
                {/* ROW 1: COPY & SHARE */}
                <div className="right-btn-row">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(entryLink)}
                  >
                    <img src="/icons/link-copy.png" alt="copy" />
                    {isZh() ? "複製連結" : "Copy Link"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: agenda?.subject,
                          url: entryLink,
                        });
                      } else {
                        alert(isZh() ? "瀏覽器不支援分享" : "Browser tidak support fitur share");
                      }
                    }}
                  >
                    <img src="/icons/share.png" alt="share" />
                    {isZh() ? "分享連結" : "Share Link"}
                  </button>
                </div>

                {/* ROW 2: PREVIEW */}
                <div className="right-btn-row">
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `/f?${fullFormCode}&preview=1&type=internal`,
                        "_blank"
                      )
                    }
                  >
                    {isZh() ? "預覽內部" : "Preview Internal"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `/f?${fullFormCode}&preview=1&type=external`,
                        "_blank"
                      )
                    }
                  >
                    {isZh() ? "預覽外部/公開" : "Preview External/Public"}
                  </button>
                </div>
              </>
            )}

            <hr />

            <div className="default-info">
              <strong>
                {isZh()
                  ? "外部/公開預設欄位"
                  : "Default External/Public Fields"}
              </strong>

              <ul>
                <li>Gender / 性別</li>
                <li>Nama / 姓名</li>
                <li>Usia / 年齡</li>
                <li>No. HP / 電話</li>
                <li>Keterangan / 備註</li>
              </ul>
            </div>
          </div>
        }
      >
        <div className="prereg-setup">
          {/* Agenda Header */}
          <div className="detail-agenda">
            <div className="da-date-main">
              {new Date(agenda.agendaDate).toLocaleDateString("id-ID", {
                weekday: "long",
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </div>

            {lunarInfo && (
              <div className="da-date-range">
                歲次{lunarInfo.lunarGanZhi}{" "}
                {lunarInfo.lunarMonth}月{lunarInfo.lunarDay}日
              </div>
            )}

            <div className="da-title">
              {agenda.subject_zh} {agenda.subject}
            </div>

            <div className="scope-box">
              <strong>{isZh() ? "報名範圍" : "Cakupan Form Registrasi"}:</strong>{" "}
              {getScopeText()}
            </div>
          </div>

          {/* Tabs */}
          <TopNavTabs
            items={[
              { key: "general", label: isZh() ? "基本設定" : "General Settings" },
              { key: "fields", label: isZh() ? "欄位設定" : "Field Configuration" },
            ]}
            activeKey={activeTab}
            onTabClick={(k) => setActiveTab(k)}
          />

          {/* GENERAL */}
          {activeTab === "general" && (
            <div className="form-header-box">
              <label>
                {isZh()
                  ? "表單代碼"
                  : "Kode Form"}
                <span style={{ paddingLeft: "1rem", fontSize: "0.8rem", color: "#64748b" }}>
                  {previewUrl}
                </span>
              </label>

              <input
                value={cleanedCode}
                onChange={(e) =>
                  setFormCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))
                }
              />

              <label>
                {isZh()
                  ? "中文標題"
                  : "Judul Form (Chinese)"}
              </label>
              <input value={titleZh} onChange={(e) => setTitleZh(e.target.value)} />

              <label>
                {isZh()
                  ? "標題（印尼文）"
                  : "Judul Form (Indonesia)"}
              </label>
              <input value={titleId} onChange={(e) => setTitleId(e.target.value)} />

              <label>
                {isZh()
                  ? "描述（中文）"
                  : "Deskripsi (Chinese)"}
              </label>
              <textarea value={descZh} onChange={(e) => setDescZh(e.target.value)} />

              <label>
                {isZh()
                  ? "描述（印尼文）"
                  : "Deskripsi (Indonesia)"}
              </label>
              <textarea value={descId} onChange={(e) => setDescId(e.target.value)} />

              <label>
                {isZh()
                  ? "填寫說明（中文）"
                  : "Petunjuk Pengisian (Chinese)"}
              </label>
              <textarea
                value={instructionZh}
                onChange={(e) => setInstructionZh(e.target.value)}
              />

              <label>
                {isZh()
                  ? "填寫說明（印尼文）"
                  : "Petunjuk Pengisian (Indonesia)"}
              </label>
              <textarea
                value={instructionId}
                onChange={(e) => setInstructionId(e.target.value)}
              />

              <div className="checkbox-row">
                <input
                  type="checkbox"
                  checked={allowRemarkOnInternal}
                  onChange={(e) => setAllowRemarkOnInternal(e.target.checked)}
                />
                <label>
                  {isZh()
                    ? "允許內部填寫備註"
                    : "Umat internal (Li En) boleh memberikan catatan (remarks)."}
                </label>
              </div>

              <div className="checkbox-row">
                <input
                  type="checkbox"
                  checked={allowRemarkOnExternal}
                  onChange={(e) => setAllowRemarkOnExternal(e.target.checked)}
                />
                <label>
                  {isZh()
                    ? "允許外部/公開填寫備註"
                    : "Umat Eksternal (Fotang lain) boleh memberikan catatan (remarks)"}
                </label>
              </div>
            </div>
          )}

          {/* FIELDS */}
          {activeTab === "fields" && (
            <>
              {fields.map((f, i) => (
                <div key={f.id} className="field-card">

                  {/* TOP */}
                  <div className="field-row">

                    <select
                      value={f.type}
                      onChange={(e) => updateField(i, "type", e.target.value)}
                    >
                      <option value="text">Text</option>
                      <option value="paragraph">Paragraph</option>
                      <option value="date">Date</option>
                      <option value="number">Number</option>
                      <option value="multiple">Multiple Choice</option>
                      <option value="checkboxes">Checkboxes</option>
                    </select>

                    <div className="field-actions">

                      <button
                        type="button"
                        onClick={() => {
                          const copy = [...fields];

                          copy.splice(i + 1, 0, {
                            ...JSON.parse(JSON.stringify(f)),
                            id: `fld_${Date.now()}`
                          });

                          setFields(copy);
                        }}
                      >
                        Duplicate
                      </button>

                      <button type="button" onClick={() => moveField(i, -1)}>
                        ↑
                      </button>

                      <button type="button" onClick={() => moveField(i, 1)}>
                        ↓
                      </button>

                      <button type="button" onClick={() => removeField(i)}>
                        ✕
                      </button>
                    </div>
                  </div>

                  {/* LABEL */}
                  <input
                    placeholder="Label 中文"
                    value={f.labelZh}
                    onChange={(e) => updateField(i, "labelZh", e.target.value)}
                  />

                  <input
                    placeholder="Label Indonesia"
                    value={f.labelId}
                    onChange={(e) => updateField(i, "labelId", e.target.value)}
                  />

                  {/* DESCRIPTION */}
                  <textarea
                    placeholder="Description 中文"
                    value={f.descriptionZh}
                    onChange={(e) =>
                      updateField(i, "descriptionZh", e.target.value)
                    }
                  />

                  <textarea
                    placeholder="Description Indonesia"
                    value={f.descriptionId}
                    onChange={(e) =>
                      updateField(i, "descriptionId", e.target.value)
                    }
                  />

                  {/* SCOPE */}
                  <div className="field-subsection">

                    <label>Scope</label>

                    <select
                      value={f.scope || "all"}
                      onChange={(e) => updateField(i, "scope", e.target.value)}
                    >
                      <option value="peserta">
                        Peserta
                      </option>

                      <option value="petugas">
                        Petugas
                      </option>

                      <option value="all">
                        All
                      </option>
                    </select>
                  </div>

                  {/* NUMBER */}
                  {f.type === "number" && (
                    <div className="field-subsection">

                      <div className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={!!f.allowDecimal}
                          onChange={(e) =>
                            updateField(i, "allowDecimal", e.target.checked)
                          }
                        />

                        <label>
                          Allow Decimal
                        </label>
                      </div>
                    </div>
                  )}

                  {/* MULTIPLE / CHECKBOX */}
                  {(f.type === "multiple" || f.type === "checkboxes") && (
                    <div className="options-box">

                      {(f.options || []).map((opt, optIndex) => (
                        <div key={optIndex} className="option-row">

                          <input
                            value={opt}
                            placeholder={`Option ${optIndex + 1}`}
                            onChange={(e) => {
                              const arr = [...(f.options || [])];
                              arr[optIndex] = e.target.value;
                              updateField(i, "options", arr);
                            }}
                          />

                          <button
                            type="button"
                            onClick={() => {
                              const arr = [...(f.options || [])];
                              arr.splice(optIndex, 1);
                              updateField(i, "options", arr);
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="mini-add-btn"
                        onClick={() => {
                          updateField(i, "options", [
                            ...(f.options || []),
                            `Option ${(f.options || []).length + 1}`
                          ]);
                        }}
                      >
                        + Add Option
                      </button>

                      <div className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={!!f.allowOther}
                          onChange={(e) =>
                            updateField(i, "allowOther", e.target.checked)
                          }
                        />

                        <label>
                          Add "Other"
                        </label>
                      </div>
                    </div>
                  )}

                  {/* REQUIRED */}
                  <div className="checkbox-row required-row">
                    <input
                      type="checkbox"
                      checked={!!f.required}
                      onChange={(e) =>
                        updateField(i, "required", e.target.checked)
                      }
                    />

                    <label>
                      Required
                    </label>
                  </div>
                </div>
              ))}

              <button type="button" className="add-btn" onClick={addField}>
                + Add Field / 新增欄位
              </button>
            </>
          )}
        </div>
        <style>{`
        .prereg-setup .detail-agenda{
          background:#fff;
          border:1px solid #e6eef8;
          border-radius:0.75rem;
          padding:1.2rem 1.5rem;
          margin-bottom:1.25rem;
          box-shadow:0 4px 14px rgba(0,0,0,0.04);
        }

        .prereg-setup .da-title{
          font-size:1.125rem;
          font-weight:700;
          margin-top:0.5rem;
        }

        .prereg-setup .da-subtitle{
          font-size:0.9rem;
          color:#64748b;
        }

        .prereg-setup .scope-box{
          margin-top:0.6rem;
          font-size:0.85rem;
          color:#334155;
        }

        /* ===== GENERAL CARD ===== */
        .prereg-setup .form-header-box{
          background:#fff;
          border-radius:0.75rem;
          padding:1.25rem 1.5rem;
          margin-bottom:1.25rem;
          box-shadow:0 6px 18px rgba(0,0,0,0.04);
          display:flex;
          flex-direction:column;
          gap:0.75rem;
        }

        .prereg-setup .form-header-box label{
          font-size:0.85rem;
          font-weight:600;
          margin-bottom:0.25rem;
        }

        .prereg-setup .form-header-box input {
          width:95%;
          padding:0.6rem 0.75rem;
          border:1px solid #dbe3ef;
          border-radius:0.5rem;
          font-size:0.9rem;
        }

        /* ===== FIELD CARD ===== */
        .prereg-setup .field-card{
          background:#fff;
          border-radius:0.75rem;
          padding:1.2rem 1.5rem;
          margin-bottom:1rem;
          box-shadow:0 6px 18px rgba(0,0,0,0.04);
          border-left:4px solid #244f8f;
        }

        .prereg-setup .field-row{
          display:flex;
          justify-content:space-between;
          align-items:center;
          margin-bottom:0.75rem;
        }

        .prereg-setup .field-row select{
          width:190px;
          padding:0.5rem 0.75rem;
          border-radius:0.5rem;
          border:1px solid #dbe3ef;
          font-size:0.85rem;
        }

        .prereg-setup .field-actions{
          display:flex;
          gap:0.5rem;
        }

        .prereg-setup .field-actions button{
          background:#f1f5f9;
          border:1px solid #dbe3ef;
          padding:0.3rem 0.6rem;
          border-radius:0.4rem;
          font-size:0.8rem;
        }

        .prereg-setup .field-card input{
          width:100%;
          padding:0.6rem 0.75rem;
          border:1px solid #dbe3ef;
          border-radius:0.5rem;
          font-size:0.9rem;
          margin-bottom:0.75rem;
        }

        /* ===== ADD BUTTON ===== */
        .prereg-setup .add-btn{
          width:100%;
          background:#f1f3f6;
          border:1px dashed #cbd5e1;
          padding:0.85rem;
          border-radius:0.6rem;
          font-weight:600;
          margin-top:0.75rem;
        }

        .prereg-setup .add-btn:hover{
          background:#e8edf3;
        }

        /* ===== CHECKBOX ===== */
        .prereg-setup .checkbox-row{
          display:flex;
          align-items:center;
          gap:0.5rem;
          margin-top:0.5rem;
        }

        .prereg-setup .checkbox-row input{
          width:1rem;
          height:1rem;
        }

        .prereg-right h4{
          margin-bottom:0.5rem;
        }

        .prereg-right .share-box{
          background:#f4f6fb;
          padding:0.75rem;
          border-radius:0.5rem;
          font-size:0.85rem;
          word-break:break-all;
          margin-bottom:0.75rem;
        }

        .prereg-right .right-btn-row{
          display:flex;
          gap:0.5rem;
          margin-bottom:0.5rem;
        }

        .prereg-right button{
          display:flex;
          align-items:center;
          gap:0.4rem;
          background:#f1f5f9;
          border:1px solid #dbe3ef;
          padding:0.4rem 0.6rem;
          border-radius:0.45rem;
          font-size:0.8rem;
          cursor:pointer;
        }

        .prereg-right button img{
          width:0.9rem;
          height:0.9rem;
        }

        .prereg-right .default-info{
          font-size:0.85rem;
        }

        .prereg-setup .field-subsection{
          margin-top:0.75rem;
        }

        .prereg-setup .field-subsection label{
          display:block;
          font-size:0.8rem;
          font-weight:600;
          margin-bottom:0.35rem;
        }

        .prereg-setup .options-box{
          margin-top:1rem;
          border-top:1px dashed #dbe3ef;
          padding-top:1rem;
        }

        .prereg-setup .option-row{
          display:flex;
          gap:0.5rem;
          margin-bottom:0.5rem;
        }

        .prereg-setup .option-row input{
          flex:1;
          margin-bottom:0;
        }

        .prereg-setup .mini-add-btn{
          background:#f8fafc;
          border:1px dashed #cbd5e1;
          border-radius:0.5rem;
          padding:0.5rem 0.75rem;
          font-size:0.85rem;
          margin-top:0.25rem;
        }

        .prereg-setup .required-row{
          margin-top:1rem;
          padding-top:0.75rem;
          border-top:1px solid #eef2f7;
        }        

        .prereg-setup .form-header-box textarea,
        .prereg-setup .field-card textarea{
          width:100%;
          padding:0.6rem 0.75rem;
          border:1px solid #dbe3ef;
          border-radius:0.5rem;
          font-size:0.9rem;
          font-family:inherit;
          line-height:1.5;
          resize:vertical;
          min-height:5rem;
          background:#fff;
          box-sizing:border-box;
        }

        .prereg-setup .field-card textarea{
          margin-bottom:0.75rem;
        }
                    
        `}</style>
      </FormLayout>
      <AgendaAttributeConfigModal
        open={showAttrModal}
        onClose={()=>setShowAttrModal(false)}
        agendaId={documentId}
        agenda={agenda}
      />      
    </>
  );
}